<template>
  <div class="app-container">
    <!-- ===================================================================
         UNA SOLA BARRA, VOCI GUIDATE DALLE ABILITAZIONI: le voci arrivano da
         `voci`, calcolate da ruolo + capability. Aggiungere un servizio non
         aggiunge un menu.

         ⚠️ QUESTO FILE È L'UNICO PROPRIETARIO DELLO STILE DELLA BARRA.
         Regole su `.el-menu--horizontal` nei fogli globali (due classi,
         specificità (0,2,0)) vincerebbero su `.main-nav` (0,1,0) e
         spegnerebbero la barra scura. Non rimettere regole `el-menu` nei
         fogli globali.
         =================================================================== -->
    <header
      v-if="store.viewMenu && voci.length"
      class="main-nav"
    >
      <RouterLink
        :to="voci[0].to"
        class="nav-brand"
        aria-label="Simba"
      >
        <img
          :src="logo"
          alt=""
          class="nav-brand__mark"
        >
        <span class="nav-brand__name">Simba</span>
      </RouterLink>

      <el-menu
        :default-active="activeIndex"
        class="nav-menu"
        mode="horizontal"
        :ellipsis="false"
      >
        <el-menu-item
          v-for="(v, i) in voci"
          :key="v.to"
          :index="String(i + 1)"
        >
          <RouterLink
            :to="v.to"
            class="routerLink"
          >
            <span class="nav-text">{{ v.label }}</span>
          </RouterLink>
        </el-menu-item>

        <el-sub-menu
          index="user-menu"
          popper-class="nav-user-popper"
        >
          <template #title>
            <el-icon class="user-icon">
              <User />
            </el-icon>
          </template>
          <el-menu-item index="user-profile">
            <RouterLink
              to="/profile"
              class="routerLink"
            >
              <span class="nav-text">Profilo</span>
            </RouterLink>
          </el-menu-item>
          <el-menu-item
            index="user-logout"
            @click="logout"
          >
            <span class="nav-text">Logout</span>
          </el-menu-item>
        </el-sub-menu>
      </el-menu>
    </header>

    <!-- Main Content -->
    <el-main class="main-content">
      <router-view />
    </el-main>
  </div>
</template>

<script>
import { useRoute, useRouter } from 'vue-router';
import { isLoggedStore } from './store/logged';
import { clearSession, isPartnerAdmin, ROLES } from './services/session';
import { computed } from 'vue';
import logo from './assets/logo-simba-RIDIMENSIONATO.png';

export default {
  name: 'App',
  setup() {
    const store = isLoggedStore();
    const route = useRoute();
    const router = useRouter();

    const logout = () => {
      // clearSession azzera anche token e refresh token
      clearSession();
      router.push('/login');
    };

    // -------------------------------------------------------------------
    //  Le voci della barra: ruolo + capability del partner, in un posto solo.
    //
    //  Capability, nei termini del dominio:
    //   - `booking_type`  -> Prenotazioni (Pickup & Delivery, Transfer)
    //   - `journey_type`  -> Spostamenti interni
    //
    //  Chi vede cosa:
    //   - tutti e tre i ruoli: Home, Agenda, Nuova prenotazione, Preventivo,
    //     Clienti, Tariffe;
    //   - nelle Tariffe dipendente e venditore leggono solo le tariffe del
    //     partner; l'amministratore vede anche quelle Simba e imposta le sue.
    // -------------------------------------------------------------------
    const voci = computed(() => {
      const out = [];
      const amministratore = isPartnerAdmin(store.role);
      const ruoloPartner =
        amministratore || store.role === ROLES.EMPLOYEE || store.role === ROLES.SALESVENDOR;
      if (!ruoloPartner) return out;

      // Home, Agenda e creazione valgono per chiunque abbia almeno un servizio:
      // coprono sia le prenotazioni sia gli spostamenti interni.
      if (store.bookingType || store.journeyType) {
        // La Home sono le corse di OGGI; l'Agenda il mese.
        out.push({ to: '/', label: 'Home' });
        out.push({ to: '/agenda', label: 'Agenda' });
        out.push({ to: '/prenotazione/nuova', label: 'Nuova prenotazione' });
      }

      if (store.bookingType) {
        // NASCOSTE, NON RIMOSSE (`nascosta: true`, vedi il filtro in fondo).
        // L'Agenda, la Home e il dettaglio `/agenda/prenotazione/:id` coprono
        // da soli ciò che queste pagine facevano. Le ROTTE restano registrate
        // e funzionanti: si toglie l'ingresso dal menu, non la funzione.
        out.push({ to: '/prenotazioni-info', label: 'Prenotazioni', nascosta: true });
        out.push({ to: '/corse', label: 'Corse', nascosta: true });

      }

      // Preventivo: Pickup & Delivery e pacchetto valet per chi ha le
      // prenotazioni, spostamento interno per chi ha gli spostamenti.
      if (store.bookingType || store.journeyType) {
        out.push({ to: '/estimate', label: 'Preventivo' });
      }

      out.push({ to: '/customer', label: 'Clienti' });

      // Tariffe per tutti i ruoli: cosa si vede lo decide il backend.
      if (store.bookingType || store.journeyType) {
        out.push({ to: '/tariffe', label: 'Tariffe' });
      }

      // Il filtro sta QUI, in fondo, e non dentro i rami: cosi' le voci
      // nascoste restano scritte accanto alle altre, con le loro condizioni
      // di ruolo e capability intatte. Per riaccenderne una si toglie il suo
      // `nascosta: true` — non si riscrive la regola di chi la vede.
      return out.filter((v) => !v.nascosta);
    });

    // -------------------------------------------------------------------
    //  Quale voce e' evidenziata.
    //
    //  Si prende la voce col percorso piu' LUNGO fra quelli che combaciano,
    //  altrimenti '/' (Home) rivendicherebbe qualsiasi pagina.
    // -------------------------------------------------------------------
    const activeIndex = computed(() => {
      let scelta = '';
      let lunghezza = -1;
      voci.value.forEach((v, i) => {
        const combacia =
          v.to === '/'
            ? route.path === '/'
            : route.path === v.to || route.path.startsWith(`${v.to}/`);
        if (combacia && v.to.length > lunghezza) {
          lunghezza = v.to.length;
          scelta = String(i + 1);
        }
      });
      return scelta;
    });

    return {
      logout,
      store,
      activeIndex,
      isPartnerAdmin,
      voci,
      logo,
    };
  },
};
</script>

<style>
/* Global Reset */
*,
*::before,
*::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

/* App Container */
.app-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

#app {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #171717;
  min-height: 100vh;
  background-color: #FAFAFA;
}

/* ===================================================================
   BARRA PRINCIPALE
   -------------------------------------------------------------------
   Il giallo Simba non sparisce: cambia mestiere. Da fondo dell'intera
   barra diventa il segno della voce attiva, cosi' smette di competere
   con i dati della pagina sotto e torna a servire per indicare.
   =================================================================== */
.main-nav {
  display: flex;
  align-items: stretch;
  gap: 20px;
  height: 56px;
  padding: 0 20px;
  background-color: #232C51;
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.06), 0 2px 10px rgba(16, 20, 40, 0.18);
  position: sticky;
  top: 0;
  z-index: 100;
}

/* --- marchio --- */
.nav-brand {
  display: flex;
  align-items: center;
  gap: 9px;
  flex: none;
  text-decoration: none !important;
}

.nav-brand__mark {
  width: 24px;
  height: 24px;
  object-fit: contain;
  display: block;
}

.nav-brand__name {
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #FFFFFF;
}

/* --- voci --- */
/* Il menu Element non porta piu' il fondo: lo fa la barra. Cosi' c'e' una
   sola superficie colorata invece di due sovrapposte. */
.main-nav .nav-menu {
  flex: 1;
  min-width: 0;
  height: 100%;
  background-color: transparent !important;
  border-bottom: none !important;

  /* Sotto una certa larghezza le voci non ci stanno (sette voci più marchio e
     menu utente superano i 375px di un telefono): scorre la barra, non la
     pagina. `:ellipsis="false"` le tiene tutte: un menu che nasconde le voci
     in eccesso nasconde proprio quelle di chi ha più servizi attivi. */
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
}

.main-nav .nav-menu::-webkit-scrollbar {
  display: none;
}

/* ⚠️ `height` e `padding` vogliono `!important`: Element li impone da
   `.el-menu--horizontal > .el-sub-menu .el-sub-menu__title`, che è (0,3,1) e
   batte qualunque selettore ragionevole scritto qui. */
.main-nav .el-menu-item,
.main-nav .el-sub-menu__title {
  height: 100% !important;
  padding: 0 14px !important;
  color: rgba(255, 255, 255, 0.7) !important;
  background-color: transparent !important;
  border-bottom: 2px solid transparent !important;
  transition: color 0.15s ease, background-color 0.15s ease;
}

.main-nav .el-menu-item:hover,
.main-nav .el-sub-menu__title:hover {
  color: #FFFFFF !important;
  background-color: rgba(255, 255, 255, 0.07) !important;
}

.main-nav .el-menu-item.is-active {
  color: #FFFFFF !important;
  border-bottom-color: #E7E43A !important;
  background-color: rgba(231, 228, 58, 0.1) !important;
}

/* Il menu utente si stacca a destra: sta all'estremo, non in mezzo alle
   voci di navigazione, perche' non e' una destinazione ma un'azione. */
.main-nav .el-sub-menu:last-child {
  margin-left: auto;
}

/* Navigation Text */
.nav-text {
  font-weight: 600;
  font-size: 0.875rem;
  letter-spacing: 0.005em;
  white-space: nowrap;
}

/* User Icon */
.user-icon {
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.85);
}

/* Router Link Reset */
.routerLink {
  text-decoration: none !important;
  color: inherit !important;
  display: block;
}

.routerLink:hover {
  text-decoration: none !important;
}

/* --- tendina del menu utente ---
   Element la monta fuori da `.main-nav` (popper appeso al body), quindi le
   regole della barra non la raggiungono: si aggancia per `popper-class`. */
.nav-user-popper .el-menu-item {
  color: #171717 !important;
  background-color: transparent !important;
}

.nav-user-popper .el-menu-item:hover {
  background-color: #F1F2F7 !important;
  color: #232C51 !important;
}

/* Main Content Area */
.main-content {
  flex: 1;
  padding: 24px;
  background-color: #FAFAFA;
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
  .main-content {
    padding: 16px;
  }

  .main-nav {
    padding: 0 12px;
    gap: 10px;
  }

  .nav-brand__name {
    display: none;
  }

  .main-nav .el-menu-item,
  .main-nav .el-sub-menu__title {
    padding: 0 10px !important;
  }

  .nav-text {
    font-size: 0.8125rem;
  }
}
</style>
